import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Stat pill — one number + label
const StatPill = ({ value, label, color, t }) => (
  <Box sx={{
    flex: 1, padding: "10px 12px", borderRadius: "3px",
    background: t.surface2, border: `1px solid ${t.border}`,
    display: "flex", flexDirection: "column", gap: "2px",
  }}>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif",
      fontSize: "22px", fontWeight: 700,
      color: color, lineHeight: 1,
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px", letterSpacing: "0.12em",
      textTransform: "uppercase", color: t.text3,
    }}>
      {label}
    </Typography>
  </Box>
);

const getCurrentQuarterStr = () => {
  const now = new Date();
  const q   = Math.ceil((now.getMonth() + 1) / 3);
  const fy  = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
  return `Q${q}-${fy}`;
};
// Utilisation bar — occupied / ceiling
const UtilBar = ({ occupied, ceiling, t }) => {
  const pct = ceiling > 0 ? Math.min((occupied / ceiling) * 100, 100) : 0;
  const barColor = pct >= 90 ? "#c84040" : pct >= 70 ? "#D6DF27" : "#28B3B4";
  // Add near the top of the file, above PoolStatusCard


  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: "4px" }}>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
          Utilisation
        </Typography>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text2 }}>
          {occupied} / {ceiling}
        </Typography>
      </Box>
      <Box sx={{ height: "5px", background: t.surface3, borderRadius: "3px", overflow: "hidden" }}>
        <Box sx={{
          height: "100%", width: `${pct}%`,
          background: barColor, borderRadius: "3px",
          transition: "width 0.4s ease",
        }} />
      </Box>
    </Box>
  );
};

const PoolStatusCard = ({ status }) => {
  const theme = useTheme();
  const t     = theme.custom;

  if (!status) return null;

  return (
    <Box sx={{ mt: "16px" }}>

      {/* Section label */}
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px", letterSpacing: "0.14em",
        textTransform: "uppercase", color: t.text3,
        borderTop: `1px solid ${t.border}`,
        paddingTop: "14px", mb: "10px",
      }}>
        Pool Status · {getCurrentQuarterStr()}
      </Typography>

      {/* Stat pills row */}
      <Box sx={{ display: "flex", gap: "6px", mb: "12px" }}>
        <StatPill value={status.free}     label="Free"     color={t.accent}  t={t} />
        <StatPill value={status.bench}    label="Bench"    color={t.gold}    t={t} />
        <StatPill value={status.deployed} label="Deployed" color="#4a9960"   t={t} />
        <StatPill value={status.retiring} label="Retiring" color="#c84040"   t={t} />
      </Box>

      {/* Utilisation bar */}
      <UtilBar occupied={status.occupied} ceiling={status.ceiling} t={t} />

      {/* Set by info */}
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px", color: t.text3, mt: "8px",
      }}>
        Set by {status.setByName} · {getCurrentQuarterStr()}
      </Typography>
    </Box>
  );
};

export default PoolStatusCard;